/**
 * FusionInstallView — branded installation page for the GienoxFusion
 * Autodesk Fusion 360 extension. Similar to fusion.adam.new/install.
 *
 * Accessible at: https://3d.gienox.com/fusion-install
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Download, ExternalLink, Terminal } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BUNDLE_URL = 'https://gienox.com/downloads/GienoxFusion.bundle.zip';

const WIN_COMMAND = `powershell -Command "Invoke-WebRequest -Uri '${BUNDLE_URL}' -OutFile \\"$env:TEMP\\GienoxFusion.bundle.zip\\"; Expand-Archive -Path \\"$env:TEMP\\GienoxFusion.bundle.zip\\" -DestinationPath \\"$env:APPDATA\\Autodesk\\Autodesk Fusion 360\\API\\AddIns\\" -Force; Write-Host 'Done — restart Fusion 360'"`;

const MAC_COMMAND = `curl -L "${BUNDLE_URL}" -o /tmp/GienoxFusion.bundle.zip && unzip -o /tmp/GienoxFusion.bundle.zip -d ~/Library/Application\\ Support/Autodesk/Autodesk\\ Fusion\\ 360/API/AddIns/ && echo "Done — restart Fusion 360"`;

function CopyBlock({ label, command, platform }: { label: string; command: string; platform: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group">
      <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-adam-neutral-400">
        <Terminal className="h-3.5 w-3.5" />
        {label}
        <span className="rounded bg-adam-neutral-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-adam-neutral-500">
          {platform}
        </span>
      </div>
      <div className="relative overflow-hidden rounded-lg border border-adam-neutral-800 bg-adam-bg-dark">
        <pre className="overflow-x-auto p-4 pr-14 text-[13px] leading-relaxed text-adam-neutral-200">
          <code>{command}</code>
        </pre>
        <button
          onClick={handleCopy}
          className="absolute right-2 top-2 rounded-md border border-adam-neutral-700 bg-adam-neutral-900 p-1.5 text-adam-neutral-400 transition-colors hover:bg-adam-neutral-800 hover:text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className="h-4 w-4 text-emerald-400" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

export default function FusionInstallView() {
  return (
    <div className="flex min-h-screen w-full flex-col bg-adam-bg-dark">
      {/* Header bar */}
      <header className="flex items-center justify-between border-b border-adam-neutral-800 px-6 py-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/Gienox-Logo.png" alt="Gienox" className="h-6" />
        </Link>
        <Link
          to="/"
          className="text-sm text-adam-neutral-400 transition-colors hover:text-white"
        >
          ← Back to app
        </Link>
      </header>

      {/* Main content */}
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-6 py-12">
        {/* Hero */}
        <div className="mb-10 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-adam-blue/20 bg-adam-blue/10 px-4 py-1.5 text-sm text-adam-blue">
            <span className="h-2 w-2 rounded-full bg-adam-blue" />
            Autodesk Fusion 360 Extension
          </div>
          <h1 className="mb-3 text-3xl font-semibold tracking-tight text-white md:text-4xl">
            Install Gienox for Fusion 360
          </h1>
          <p className="text-base text-adam-neutral-300">
            Your AI copilot for Autodesk Fusion 360. Design parametric 3D
            models with AI, directly inside Fusion.
          </p>
        </div>

        {/* Section 1: One-liner install */}
        <section className="mb-10 rounded-xl border border-adam-neutral-800 bg-adam-bg-secondary-dark p-6">
          <h2 className="mb-1 text-lg font-medium text-white">
            One-liner install
            <span className="ml-2 text-sm font-normal text-adam-neutral-400">
              (recommended)
            </span>
          </h2>
          <p className="mb-5 text-sm text-adam-neutral-400">
            Paste in your terminal. Drops the add-in into Fusion's standard
            AddIns folder automatically.
          </p>
          <div className="space-y-4">
            <CopyBlock label="Windows (PowerShell)" command={WIN_COMMAND} platform="Windows" />
            <CopyBlock label="macOS (Terminal)" command={MAC_COMMAND} platform="macOS" />
          </div>
        </section>

        {/* Section 2: Manual install */}
        <section className="mb-10 rounded-xl border border-adam-neutral-800 bg-adam-bg-secondary-dark p-6">
          <h2 className="mb-1 text-lg font-medium text-white">
            Manual install
          </h2>
          <p className="mb-5 text-sm text-adam-neutral-400">
            Prefer not to paste a command? Download the bundle and drop it into
            Fusion's plug-in folder.
          </p>

          <a href={BUNDLE_URL} download>
            <Button className="mb-6 gap-2">
              <Download className="h-4 w-4" />
              Download GienoxFusion.bundle.zip
            </Button>
          </a>

          <ol className="space-y-3 text-sm text-adam-neutral-200">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-adam-blue/15 text-xs font-semibold text-adam-blue">
                1
              </span>
              <span>
                Unzip the download. You should see a{' '}
                <code className="rounded bg-adam-neutral-800 px-1.5 py-0.5 text-xs text-adam-neutral-200">
                  GienoxFusion.bundle
                </code>{' '}
                folder containing a{' '}
                <code className="rounded bg-adam-neutral-800 px-1.5 py-0.5 text-xs text-adam-neutral-200">
                  Contents
                </code>{' '}
                subfolder.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-adam-blue/15 text-xs font-semibold text-adam-blue">
                2
              </span>
              <div>
                <span>Move the entire folder to Fusion's AddIns directory:</span>
                <div className="mt-2 space-y-1">
                  <div className="rounded bg-adam-neutral-900 px-3 py-1.5 text-xs text-adam-neutral-300">
                    <span className="text-adam-neutral-500">Windows: </span>
                    <code>%APPDATA%\Autodesk\Autodesk Fusion 360\API\AddIns\</code>
                  </div>
                  <div className="rounded bg-adam-neutral-900 px-3 py-1.5 text-xs text-adam-neutral-300">
                    <span className="text-adam-neutral-500">macOS: </span>
                    <code>~/Library/Application Support/Autodesk/Autodesk Fusion 360/API/AddIns/</code>
                  </div>
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-adam-blue/15 text-xs font-semibold text-adam-blue">
                3
              </span>
              <span>
                Quit and reopen Fusion 360.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-adam-blue/15 text-xs font-semibold text-adam-blue">
                4
              </span>
              <span>
                Press{' '}
                <kbd className="rounded border border-adam-neutral-700 bg-adam-neutral-800 px-1.5 py-0.5 text-xs">
                  Shift+S
                </kbd>{' '}
                → <strong>Add-Ins</strong> tab → find{' '}
                <strong>GienoxFusion</strong> → click <strong>Run</strong> and
                tick <strong>Run on Startup</strong>.
              </span>
            </li>
          </ol>
        </section>

        {/* Footer notes */}
        <div className="text-center text-sm text-adam-neutral-500">
          <p className="mb-2">
            Requires Autodesk Fusion 360 on macOS or Windows. Free tier
            included — no credit card to start.
          </p>
          <p>
            New to Gienox?{' '}
            <Link to="/" className="text-adam-blue hover:text-adam-blue/80">
              Start building at 3d.gienox.com
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
